import { ArrowDownLeftIcon, PiggyBankIcon } from '@phosphor-icons/react'
import { type PlainDate, formatMoney } from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import { ApiError, nombreDe } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'
import { useAccounts, useHousehold, useIngresos, useRegistrarIngreso } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Registrar dinero que ya entró.
 *
 * Existe porque la app sabía registrar lo que sale y no lo que entra fuera de
 * la nómina. Un bono, la venta de algo, un pago suelto: entraba en la cuenta y
 * la app no se enteraba, así que el disponible se quedaba corto y no había
 * forma de cuadrarlo.
 *
 * Dos preguntas de verdad —cuánto y a qué cuenta cayó— y el resto en su sitio
 * con valores razonables. **La cuenta es obligatoria y no se adivina**: si cae
 * en la de gasto sube tu disponible y si cae en la de ahorro engorda tu ahorro,
 * y esa diferencia la decide quien cobró, no la app.
 *
 * Si además el dinero viene de un ingreso ya dado de alta —«Proyectos
 * freelance»—, elegirlo lo mete en su historial. Es lo que permite decir
 * después «en los últimos seis meses cobraste $18,400 al mes en promedio», que
 * es el único número con el que se planea sin sueldo fijo.
 */
export function RegistrarIngreso() {
  const navigate = useNavigate()
  const { membership, profile } = useSession()
  const hogar = useHousehold()
  const cuentas = useAccounts()
  const misIngresos = useIngresos(membership.id)
  const registrar = useRegistrarIngreso()

  const [cents, setCents] = useState(0)
  const [descripcion, setDescripcion] = useState('')
  const [cuentaId, setCuentaId] = useState('')
  const [fuenteId, setFuenteId] = useState('')
  const [duenoId, setDuenoId] = useState(membership.id)
  const [fecha, setFecha] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [error, setError] = useState<string | null>(null)

  const integrantes = hogar.data?.members ?? []
  const esAdmin = membership.role === 'ADMIN'

  /**
   * Dónde puede caer el dinero.
   *
   * Los vales quedan fuera: nadie deposita un bono en la tarjeta de despensa, y
   * ofrecerlo solo invita a descuadrar la bolsa que la app mantiene aparte.
   */
  const destinos = useMemo(
    () =>
      (cuentas.data ?? []).filter((c) => c.archivedAt === null && c.type !== 'MEAL_VOUCHER'),
    [cuentas.data],
  )

  // Las fuentes sin calendario son las únicas que ganan algo con esto: en una
  // nómina, el cobro se confirma desde su propio aviso.
  const fuentes = useMemo(
    () => (misIngresos.data ?? []).filter((f) => f.frequency === 'IRREGULAR' && f.active),
    [misIngresos.data],
  )

  const cuentaElegida = destinos.find((c) => c.id === cuentaId) ?? null
  const esAhorro = cuentaElegida?.type === 'SAVINGS' || cuentaElegida?.type === 'INVESTMENT'
  const listo = cents > 0 && cuentaId !== '' && /^\d{4}-\d{2}-\d{2}$/.test(fecha)

  const enviar = async (): Promise<void> => {
    setError(null)
    try {
      await registrar.mutateAsync({
        amountCents: cents,
        occurredOn: fecha as PlainDate,
        depositToAccountId: cuentaId,
        memberId: duenoId,
        incomeSourceId: fuenteId || null,
        description: descripcion.trim() || null,
      })
      haptic('success')
      navigate('/')
    } catch (cause) {
      haptic('error')
      setError(
        cause instanceof ApiError ? cause.message : 'No pudimos registrar el ingreso.',
      )
    }
  }

  return (
    <FormSheet
      tinte="butter"
      titulo="Registrar un ingreso"
      onCerrar={() => navigate(-1)}
      accion={
        <Guardar onClick={() => void enviar()} disabled={!listo} guardando={registrar.isPending}>
          Registrar
        </Guardar>
      }
    >
      <AvisoError mensaje={error} />

      <Campo label="Cuánto entró">
        <Monto cents={cents} onChange={setCents} />
      </Campo>

      <Campo
        label="A qué cuenta cayó"
        ayuda={
          esAhorro
            ? 'Va a una cuenta de ahorro: sube tu ahorro, no tu dinero libre.'
            : 'Sube el saldo de esa cuenta y con él tu dinero disponible.'
        }
      >
        {cuentas.isPending ? (
          <div className="h-[52px] animate-pulse rounded-card bg-surface-sunken" />
        ) : destinos.length === 0 ? (
          <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[13px] text-ink-soft">
            Todavía no tienes cuentas. Agrega una en Ajustes para poder decir dónde cayó el
            dinero.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {destinos.map((c) => {
              const activo = c.id === cuentaId
              const ahorro = c.type === 'SAVINGS' || c.type === 'INVESTMENT'
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    haptic('selection')
                    setCuentaId(c.id)
                  }}
                  aria-pressed={activo}
                  className={cn(
                    'flex min-h-[56px] items-center gap-3 rounded-card border px-3.5 py-2.5 text-left',
                    'transition-colors duration-150 active:scale-[0.99]',
                    activo ? 'border-primary bg-primary-soft' : 'border-line bg-surface',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-9 shrink-0 items-center justify-center rounded-full',
                      ahorro ? 'bg-income-soft text-income-text' : 'bg-surface-sunken text-ink-soft',
                    )}
                  >
                    {ahorro ? <PiggyBankIcon size={17} /> : <ArrowDownLeftIcon size={17} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-ink">
                      {c.name}
                    </span>
                    <span data-money className="block truncate text-[12px] text-ink-soft">
                      {formatMoney(c.balanceCents, { hideZeroCents: true })}
                      {ahorro ? ' · ahorro' : ''}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </Campo>

      <Campo label="Qué fue" ayuda="Para reconocerlo después en tus movimientos.">
        <Texto
          value={descripcion}
          onChange={setDescripcion}
          placeholder="Venta de la bici, bono, préstamo devuelto…"
          maxLength={140}
        />
      </Campo>

      <Campo label="Cuándo entró">
        <Texto type="date" value={fecha} onChange={setFecha} />
      </Campo>

      {fuentes.length > 0 ? (
        <Campo
          label="¿Es de alguno de tus ingresos?"
          ayuda="Si lo eliges, este cobro cuenta para su promedio de los últimos meses."
        >
          <Segmentos
            columnas={2}
            value={fuenteId}
            onChange={setFuenteId}
            opciones={
              [
                { value: '', label: 'Suelto', detalle: 'No se repite' },
                ...fuentes.map((f) => ({ value: f.id, label: f.name })),
              ] satisfies OpcionSegmento<string>[]
            }
          />
        </Campo>
      ) : null}

      {integrantes.length > 1 && esAdmin ? (
        <Campo label="De quién es" ayuda="El dinero es de quien lo cobró, caiga donde caiga.">
          <Segmentos
            columnas={2}
            value={duenoId}
            onChange={setDuenoId}
            opciones={integrantes.map((m) => ({
              value: m.id,
              label: nombreDe(m).split(' ')[0]!,
              detalle: m.id === membership.id ? 'Tú' : personPalette(m.personColorToken).label,
            }))}
          />
        </Campo>
      ) : null}

      {cents > 0 && cuentaElegida ? (
        <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[13px] leading-snug text-ink-soft">
          <span data-money className="font-semibold text-ink">
            {formatMoney(cents, { hideZeroCents: true })}
          </span>{' '}
          entran a <span className="font-semibold text-ink">{cuentaElegida.name}</span>
          {esAhorro
            ? '. Como es una cuenta de ahorro, no sube tu dinero libre.'
            : `, así que ${duenoId === membership.id ? 'tu' : 'su'} disponible sube en ese mismo monto.`}
        </p>
      ) : null}

      <p className="px-1 text-[12px] leading-snug text-ink-faint">
        {profile.displayName ? null : null}
        ¿Cobras sin fecha fija? Da de alta ese ingreso en Ajustes como «sin calendario» y
        elígelo aquí cada vez: la app te dirá cuánto cobras al mes en promedio.
      </p>
    </FormSheet>
  )
}
